function createMaterial(Three, color, options = {}) {
  const material = new Three.MeshStandardMaterial({
    color,
    metalness: options.metalness ?? 0.72,
    roughness: options.roughness ?? 0.3,
    emissive: options.emissive ?? 0x000000,
    emissiveIntensity: options.emissiveIntensity ?? 0,
    transparent: options.transparent ?? false,
    opacity: options.opacity ?? 1,
  });
  if (typeof material.onBuild !== "function") material.onBuild = () => {};
  return material;
}

function addMesh(group, Three, geometry, material, position, rotation, scale) {
  const object = new Three.Mesh(geometry, material);
  if (position) object.position.set(...position);
  if (rotation) object.rotation.set(...rotation);
  if (scale) object.scale.set(...scale);
  group.add(object);
  return object;
}

function addWing(group, Three, side, style, hullMaterial, accentMaterial) {
  const spread = style === "razor" ? 1.22 : style === "delta" ? 1.08 : 0.98;
  const sweep = style === "fork" ? 0.62 : 0.42;

  addMesh(
    group,
    Three,
    new Three.BoxGeometry(1.55 * spread, 0.13, 0.72),
    hullMaterial,
    [side * 1.05, -0.08, 0.03],
    [0, side * -0.38, side * -0.035],
  );
  addMesh(
    group,
    Three,
    new Three.BoxGeometry(0.95 * spread, 0.11, 0.48),
    hullMaterial,
    [side * 1.6, -0.09, 0.44 + sweep * 0.2],
    [0, side * -0.68, side * -0.055],
  );
  addMesh(
    group,
    Three,
    new Three.BoxGeometry(0.08, 0.1, 1.45),
    accentMaterial,
    [side * 1.18, 0.02, 0.17],
    [0, side * -0.38, 0],
  );

  if (style === "ring") {
    addMesh(
      group,
      Three,
      new Three.TorusGeometry(0.42, 0.06, 8, 22),
      accentMaterial,
      [side * 1.72, 0, 0.38],
      [Math.PI / 2, 0, 0],
    );
  }
}

function addJungleArmour(group, Three, leafMaterial, vineMaterial) {
  const leaves = [
    [-1.28, 0.13, 0.3, -0.35],
    [-0.88, 0.24, -0.2, 0.42],
    [-0.48, 0.39, 0.25, -0.22],
    [0.52, 0.38, 0.22, 0.28],
    [0.9, 0.22, -0.16, -0.4],
    [1.3, 0.12, 0.32, 0.37],
  ];
  for (const [x, y, z, angle] of leaves) {
    addMesh(
      group,
      Three,
      new Three.OctahedronGeometry(0.22, 0),
      leafMaterial,
      [x, y, z],
      [0, angle, 0.18],
      [1.65, 0.3, 0.75],
    );
  }

  for (let index = 0; index < 6; index += 1) {
    addMesh(
      group,
      Three,
      new Three.CylinderGeometry(0.028, 0.035, 0.62, 6),
      vineMaterial,
      [-1.15 + index * 0.45, 0.34 + (index % 2) * 0.08, 0.18 - index * 0.07],
      [0.2, 0, 1.1],
    );
  }
}

function addFin(group, Three, style, hullMaterial, accentMaterial) {
  if (style === "none") return;
  const positions = style === "twin" ? [-0.46, 0.46] : [0];
  for (const x of positions) {
    addMesh(
      group,
      Three,
      new Three.BoxGeometry(0.12, style === "sail" ? 1.08 : 0.72, 0.82),
      style === "sail" ? accentMaterial : hullMaterial,
      [x, 0.42, 0.72],
      [0.18, 0, x ? x * -0.14 : 0],
      [1, 1, 0.52],
    );
  }
}

export function buildShip(config, Three) {
  if (!Three?.Group || !Three?.MeshStandardMaterial) return null;
  window.__orbitrushThreeContext = Three;

  const group = new Three.Group();
  group.name = "OrbitrushUpgradedShip";

  const hullColor = config.hullColor ?? 0xc8d8ee;
  const glowColor = config.glowColor ?? 0x00d2ff;
  const hullMaterial = createMaterial(Three, hullColor, {
    metalness: 0.86,
    roughness: 0.22,
    emissive: 0x102230,
    emissiveIntensity: 0.28,
  });
  const darkMaterial = createMaterial(Three, 0x101a22, {
    metalness: 0.92,
    roughness: 0.26,
  });
  const accentMaterial = createMaterial(Three, glowColor, {
    metalness: 0.7,
    roughness: 0.2,
    emissive: glowColor,
    emissiveIntensity: 1,
  });
  const glassMaterial = createMaterial(Three, 0x173c48, {
    metalness: 0.35,
    roughness: 0.08,
    emissive: glowColor,
    emissiveIntensity: 0.34,
    transparent: true,
    opacity: 0.9,
  });
  const leafMaterial = createMaterial(Three, 0x2f7d42, {
    metalness: 0.12,
    roughness: 0.84,
    emissive: 0x0b2413,
    emissiveIntensity: 0.22,
  });
  const vineMaterial = createMaterial(Three, 0x87bf43, {
    metalness: 0.08,
    roughness: 0.9,
  });

  const width =
    config.hull === "hammer"
      ? 1.28
      : config.hull === "needle"
        ? 0.74
        : config.hull === "manta"
          ? 1.18
          : config.hull === "hauler"
            ? 1.22
            : 1;
  const length = config.hull === "needle" ? 1.2 : config.hull === "hauler" ? 0.92 : 1;

  addMesh(
    group,
    Three,
    new Three.CylinderGeometry(0.43 * width, 0.56 * width, 2.45 * length, 18),
    hullMaterial,
    [0, 0.02, 0.02],
    [Math.PI / 2, 0, 0],
    [1, 0.9, 1],
  );
  addMesh(
    group,
    Three,
    new Three.ConeGeometry(0.47 * width, 1.15 * length, 18),
    hullMaterial,
    [0, 0.02, -1.72 * length],
    [-Math.PI / 2, 0, 0],
  );
  addMesh(
    group,
    Three,
    new Three.SphereGeometry(0.48, 18, 10),
    glassMaterial,
    [0, 0.32, -0.52],
    [0, 0, 0],
    [0.82 * width, 0.48, 1.08],
  );
  addMesh(
    group,
    Three,
    new Three.BoxGeometry(0.82 * width, 0.16, 1.28),
    darkMaterial,
    [0, -0.29, 0.42],
  );

  addWing(group, Three, -1, config.wing, hullMaterial, accentMaterial);
  addWing(group, Three, 1, config.wing, hullMaterial, accentMaterial);

  for (const side of [-1, 1]) {
    addMesh(
      group,
      Three,
      new Three.CylinderGeometry(0.25, 0.33, 1.18, 16),
      darkMaterial,
      [0.74 * side, -0.06, 0.78],
      [Math.PI / 2, 0, 0],
    );
    addMesh(
      group,
      Three,
      new Three.TorusGeometry(0.27, 0.065, 8, 18),
      accentMaterial,
      [0.74 * side, -0.06, 1.4],
    );
    addMesh(
      group,
      Three,
      new Three.CylinderGeometry(0.13, 0.19, 0.42, 10),
      accentMaterial,
      [0.74 * side, -0.06, 1.5],
      [Math.PI / 2, 0, 0],
    );
  }

  addFin(group, Three, config.fin, hullMaterial, accentMaterial);

  for (const side of [-1, 1]) {
    for (let index = 0; index < 3; index += 1) {
      addMesh(
        group,
        Three,
        new Three.BoxGeometry(0.17, 0.075, 0.25),
        darkMaterial,
        [side * (0.35 + index * 0.22), 0.28, 0.32 + index * 0.14],
        [0, side * 0.18, 0],
      );
    }
  }

  if (config.hull === "crystal") {
    for (const side of [-1, 1]) {
      addMesh(
        group,
        Three,
        new Three.OctahedronGeometry(0.42, 0),
        accentMaterial,
        [0.9 * side, 0.18, 0.2],
        [0.2, 0, 0.2],
        [0.45, 0.8, 1.65],
      );
    }
  }

  if (config.hull === "orbital") {
    addMesh(
      group,
      Three,
      new Three.TorusGeometry(1.18, 0.08, 8, 36),
      accentMaterial,
      [0, 0.02, 0.15],
      [Math.PI / 2, 0, 0],
    );
  }

  if (config.hull === "dart" && config.pattern === "clean") {
    addJungleArmour(group, Three, leafMaterial, vineMaterial);
  }

  group.scale.setScalar(0.92);
  return group;
}
